import React, { useState, useMemo } from 'react';
import { useRestoMod } from '../../context/RestoModContext';
import {
  RestorationTask,
  TaskStatus,
  TaskPriority,
  RestorationPhase,
  RESTORATION_PHASES,
} from '../../types/teardown';
import { MechanicalButton } from '../common/MechanicalButton';
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  Plus,
  Search,
  Filter,
  Layers,
  User,
  Edit2,
  Trash2,
  ArrowRight,
  X,
  PlayCircle,
  AlertOctagon,
} from 'lucide-react';

export interface TaskTrackerProps {
  phaseFilter?: RestorationPhase;
  subAssemblyFilter?: string;
  className?: string;
}

const STATUS_CONFIG: Record<
  TaskStatus,
  { label: string; badgeClass: string; icon: React.ReactNode }
> = {
  todo: {
    label: 'To Do',
    badgeClass: 'bg-shop-darkest text-shop-muted border-shop-border',
    icon: <Clock className="w-3.5 h-3.5" />,
  },
  in_progress: {
    label: 'In Progress',
    badgeClass: 'bg-safety-blue/20 text-safety-blue border-safety-blue/40',
    icon: <PlayCircle className="w-3.5 h-3.5" />,
  },
  blocked: {
    label: 'Blocked / Delay',
    badgeClass: 'bg-safety-red/20 text-safety-red border-safety-red/50 animate-pulse',
    icon: <AlertOctagon className="w-3.5 h-3.5" />,
  },
  completed: {
    label: 'Completed',
    badgeClass: 'bg-safety-green/20 text-safety-green border-safety-green/40',
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
  },
};

const PRIORITY_CONFIG: Record<TaskPriority, { label: string; badgeClass: string }> = {
  low: {
    label: 'Low',
    badgeClass: 'bg-shop-well text-shop-muted border-shop-border',
  },
  medium: {
    label: 'Medium',
    badgeClass: 'bg-safety-blue/15 text-safety-blue border-safety-blue/30',
  },
  high: {
    label: 'High Priority',
    badgeClass: 'bg-safety-amber/20 text-safety-amber border-safety-amber/40',
  },
  critical: {
    label: 'CRITICAL',
    badgeClass: 'bg-safety-red/25 text-safety-red border-safety-red/60 font-bold',
  },
};

export const TaskTracker: React.FC<TaskTrackerProps> = ({
  phaseFilter,
  subAssemblyFilter,
  className = '',
}) => {
  const { state, dispatch } = useRestoMod();
  const activePhase = phaseFilter || state.activePhase;

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [filterByPhase, setFilterByPhase] = useState<boolean>(true);

  // Modals
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<RestorationTask | null>(null);
  const [deletingTask, setDeletingTask] = useState<RestorationTask | null>(null);

  // Form states
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPhase, setFormPhase] = useState<RestorationPhase>(activePhase);
  const [formSubAssemblyId, setFormSubAssemblyId] = useState<string>('');
  const [formStatus, setFormStatus] = useState<TaskStatus>('todo');
  const [formPriority, setFormPriority] = useState<TaskPriority>('medium');
  const [formEstimatedHours, setFormEstimatedHours] = useState<number>(4);
  const [formActualHours, setFormActualHours] = useState<number>(0);
  const [formAssignedTo, setFormAssignedTo] = useState<string>('Marcus Vance');
  const [formBlockingReason, setFormBlockingReason] = useState<string>('');

  // Filtered tasks
  const filteredTasks = useMemo(() => {
    return state.tasks.filter((task) => {
      if (filterByPhase && task.phase !== activePhase) return false;
      if (subAssemblyFilter && task.subAssemblyId !== subAssemblyFilter) return false;
      if (selectedStatus !== 'all' && task.status !== selectedStatus) return false;
      if (selectedPriority !== 'all' && task.priority !== selectedPriority) return false;
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matchesTitle = task.title.toLowerCase().includes(q);
        const matchesDesc = task.description.toLowerCase().includes(q);
        const matchesTech = (task.assignedTo || '').toLowerCase().includes(q);
        return matchesTitle || matchesDesc || matchesTech;
      }
      return true;
    });
  }, [
    state.tasks,
    filterByPhase,
    activePhase,
    subAssemblyFilter,
    selectedStatus,
    selectedPriority,
    searchTerm,
  ]);

  // Rollup metrics
  const totalEstHours = filteredTasks.reduce((acc, t) => acc + (t.estimatedHours || 0), 0);
  const totalActHours = filteredTasks.reduce((acc, t) => acc + (t.actualHours || 0), 0);
  const hoursVariance = totalActHours - totalEstHours;
  const completedCount = filteredTasks.filter((t) => t.status === 'completed').length;
  const blockedCount = filteredTasks.filter((t) => t.status === 'blocked').length;

  // Open Add modal
  const handleOpenAddModal = () => {
    setFormTitle('');
    setFormDescription('');
    setFormPhase(activePhase);
    setFormSubAssemblyId(state.subAssemblies[0]?.id || '');
    setFormStatus('todo');
    setFormPriority('medium');
    setFormEstimatedHours(4);
    setFormActualHours(0);
    setFormAssignedTo('Marcus Vance');
    setFormBlockingReason('');
    setIsAddModalOpen(true);
  };

  // Open Edit modal
  const handleOpenEditModal = (task: RestorationTask) => {
    setEditingTask(task);
    setFormTitle(task.title);
    setFormDescription(task.description);
    setFormPhase(task.phase);
    setFormSubAssemblyId(task.subAssemblyId || '');
    setFormStatus(task.status);
    setFormPriority(task.priority);
    setFormEstimatedHours(task.estimatedHours);
    setFormActualHours(task.actualHours);
    setFormAssignedTo(task.assignedTo || '');
    setFormBlockingReason(task.blockingReason || '');
  };

  // Save New Task
  const handleSaveNewTask = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle.trim()) return;

    const newTask: RestorationTask = {
      id: `task_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      projectId: state.project.id,
      phase: formPhase,
      subAssemblyId: formSubAssemblyId || undefined,
      title: formTitle.trim(),
      description: formDescription.trim(),
      status: formStatus,
      priority: formPriority,
      estimatedHours: Math.max(0, formEstimatedHours),
      actualHours: Math.max(0, formActualHours),
      assignedTo: formAssignedTo.trim() || undefined,
      blockingReason: formStatus === 'blocked' ? formBlockingReason.trim() || undefined : undefined,
      completedAt: formStatus === 'completed' ? new Date().toISOString() : undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    dispatch({ type: 'ADD_TASK', payload: newTask });
    setIsAddModalOpen(false);
  };

  // Save Edit Task
  const handleSaveEditTask = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTask || !formTitle.trim()) return;

    const updated: RestorationTask = {
      ...editingTask,
      phase: formPhase,
      subAssemblyId: formSubAssemblyId || undefined,
      title: formTitle.trim(),
      description: formDescription.trim(),
      status: formStatus,
      priority: formPriority,
      estimatedHours: Math.max(0, formEstimatedHours),
      actualHours: Math.max(0, formActualHours),
      assignedTo: formAssignedTo.trim() || undefined,
      blockingReason: formStatus === 'blocked' ? formBlockingReason.trim() || undefined : undefined,
      completedAt:
        formStatus === 'completed'
          ? editingTask.completedAt || new Date().toISOString()
          : undefined,
      updatedAt: new Date().toISOString(),
    };

    dispatch({ type: 'UPDATE_TASK', payload: updated });
    setEditingTask(null);
  };

  // Quick Status Advance (todo -> in_progress -> completed)
  const handleAdvanceStatus = (task: RestorationTask) => {
    const cycle: Record<TaskStatus, TaskStatus> = {
      todo: 'in_progress',
      in_progress: 'completed',
      blocked: 'in_progress',
      completed: 'todo',
    };
    const nextStatus = cycle[task.status];
    dispatch({
      type: 'UPDATE_TASK',
      payload: {
        ...task,
        status: nextStatus,
        completedAt: nextStatus === 'completed' ? new Date().toISOString() : undefined,
        updatedAt: new Date().toISOString(),
      },
    });
  };

  // Quick Hours Adjustment (+0.5 hr or -0.5 hr)
  const handleAdjustHours = (task: RestorationTask, delta: number) => {
    const newHours = Math.max(0, (task.actualHours || 0) + delta);
    dispatch({
      type: 'UPDATE_TASK',
      payload: {
        ...task,
        actualHours: Math.round(newHours * 10) / 10,
        updatedAt: new Date().toISOString(),
      },
    });
  };

  // Confirm delete
  const handleConfirmDelete = () => {
    if (!deletingTask) return;
    dispatch({ type: 'DELETE_TASK', payload: deletingTask.id });
    setDeletingTask(null);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* 1. Header Rollup Statistics & Toolbar */}
      <div className="bg-shop-panel border border-shop-border rounded p-4 shadow-machined space-y-4">
        {/* Top Rollup Metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-mono">
          <div className="bg-shop-darkest p-3 rounded border border-shop-border">
            <span className="text-shop-muted text-[10px] uppercase block">TASKS COMPLETED</span>
            <div className="text-lg font-bold text-shop-highlight">
              {completedCount} / {filteredTasks.length}
            </div>
            <span className="text-[10px] text-shop-muted">
              {filteredTasks.length > 0
                ? Math.round((completedCount / filteredTasks.length) * 100)
                : 0}
              % phase task progress
            </span>
          </div>

          <div className="bg-shop-darkest p-3 rounded border border-shop-border">
            <span className="text-shop-muted text-[10px] uppercase block">HOURS ESTIMATED</span>
            <div className="text-lg font-bold text-shop-light font-mono">
              {totalEstHours.toFixed(1)} hrs
            </div>
            <span className="text-[10px] text-shop-muted">Budgeted labor</span>
          </div>

          <div className="bg-shop-darkest p-3 rounded border border-shop-border">
            <span className="text-shop-muted text-[10px] uppercase block">HOURS LOGGED</span>
            <div className="text-lg font-bold text-safety-amber font-mono">
              {totalActHours.toFixed(1)} hrs
            </div>
            <span
              className={`text-[10px] font-bold ${
                hoursVariance <= 0 ? 'text-safety-green' : 'text-safety-red'
              }`}
            >
              {hoursVariance <= 0
                ? `${Math.abs(hoursVariance).toFixed(1)} hrs favorable`
                : `+${hoursVariance.toFixed(1)} hrs variance`}
            </span>
          </div>

          <div className="bg-shop-darkest p-3 rounded border border-shop-border">
            <span className="text-shop-muted text-[10px] uppercase block">BLOCKED TASKS</span>
            <div
              className={`text-lg font-bold ${
                blockedCount > 0 ? 'text-safety-red animate-pulse' : 'text-safety-green'
              }`}
            >
              {blockedCount}
            </div>
            <span className="text-[10px] text-shop-muted">
              {blockedCount > 0 ? 'Requires attention / parts' : 'Zero blockers'}
            </span>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="flex items-center gap-3 flex-wrap justify-between pt-2 border-t border-shop-border">
          <div className="flex items-center gap-3 flex-wrap flex-1">
            {/* Search Input */}
            <div className="relative min-w-[200px] flex-1 max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-shop-muted pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search task title, tech, notes..."
                className="w-full min-h-[44px] pl-9 pr-11 py-2 bg-shop-darkest border border-shop-border rounded text-xs text-shop-light placeholder:text-shop-muted focus:outline-none focus:border-safety-amber font-mono"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-0 top-1/2 -translate-y-1/2 min-h-[44px] min-w-[44px] flex items-center justify-center text-shop-muted hover:text-shop-light cursor-pointer"
                  aria-label="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Phase Toggle */}
            <button
              type="button"
              onClick={() => setFilterByPhase(!filterByPhase)}
              className={`min-h-[44px] px-3 py-2 rounded text-xs font-mono font-bold uppercase border transition-all duration-100 flex items-center gap-2 cursor-pointer ${
                filterByPhase
                  ? 'bg-safety-amber text-shop-darkest border-safety-amberDark shadow-sm'
                  : 'bg-shop-dark text-shop-muted border-shop-border hover:text-shop-light hover:bg-shop-well'
              }`}
            >
              <Filter className="w-3.5 h-3.5" />
              <span>{filterByPhase ? `Current Phase (${RESTORATION_PHASES[activePhase]?.shortCode})` : 'All Phases'}</span>
            </button>

            {/* Status Select */}
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs text-shop-light focus:outline-none focus:border-safety-amber font-mono cursor-pointer"
            >
              <option value="all">All Statuses</option>
              {(Object.keys(STATUS_CONFIG) as TaskStatus[]).map((st) => (
                <option key={st} value={st}>
                  {STATUS_CONFIG[st].label}
                </option>
              ))}
            </select>

            {/* Priority Select */}
            <select
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
              className="min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs text-shop-light focus:outline-none focus:border-safety-amber font-mono cursor-pointer"
            >
              <option value="all">All Priorities</option>
              {(Object.keys(PRIORITY_CONFIG) as TaskPriority[]).map((pr) => (
                <option key={pr} value={pr}>
                  {PRIORITY_CONFIG[pr].label}
                </option>
              ))}
            </select>
          </div>

          {/* Add Task Button (Min 44px) */}
          <MechanicalButton
            variant="primary"
            size="default"
            icon={<Plus className="w-4 h-4" />}
            onClick={handleOpenAddModal}
          >
            Add Task
          </MechanicalButton>
        </div>
      </div>

      {/* 2. Tasks List */}
      {filteredTasks.length === 0 ? (
        <div className="bg-shop-dark border border-shop-border rounded p-12 text-center space-y-3">
          <div className="w-12 h-12 bg-shop-panel rounded-full flex items-center justify-center mx-auto text-shop-muted border border-shop-border">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-shop-highlight">
            No Tasks Found
          </h3>
          <p className="text-xs text-shop-muted max-w-md mx-auto">
            {searchTerm || selectedStatus !== 'all' || selectedPriority !== 'all'
              ? 'No restoration tasks match your active filter criteria. Try resetting filters.'
              : `No tasks registered for Phase ${RESTORATION_PHASES[activePhase]?.sequence} yet. Add action items to track technician labor hours and milestones.`}
          </p>
          <div className="pt-2">
            <MechanicalButton variant="secondary" size="sm" onClick={handleOpenAddModal}>
              Create First Task
            </MechanicalButton>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTasks.map((task) => {
            const statusMeta = STATUS_CONFIG[task.status];
            const priorityMeta = PRIORITY_CONFIG[task.priority];
            const matchingSub = state.subAssemblies.find((s) => s.id === task.subAssemblyId);
            const hourDiff = task.actualHours - task.estimatedHours;

            return (
              <div
                key={task.id}
                className="bg-shop-dark border border-shop-border rounded p-4 shadow-machined space-y-3 hover:bg-shop-panel/50 transition-all duration-150"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  {/* Status, Priority, & Title */}
                  <div className="space-y-1.5 flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`text-xs font-mono font-bold px-2 py-0.5 rounded border uppercase flex items-center gap-1.5 ${statusMeta.badgeClass}`}
                      >
                        {statusMeta.icon}
                        <span>{statusMeta.label}</span>
                      </span>

                      <span
                        className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${priorityMeta.badgeClass}`}
                      >
                        {priorityMeta.label}
                      </span>

                      <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-shop-darkest text-shop-muted border border-shop-border">
                        {RESTORATION_PHASES[task.phase]?.shortCode}
                      </span>

                      {matchingSub && (
                        <span className="text-[10px] font-mono text-safety-amber bg-shop-well px-2 py-0.5 rounded border border-shop-border flex items-center gap-1">
                          <Layers className="w-3 h-3" />
                          [{matchingSub.binNumber}] {matchingSub.name}
                        </span>
                      )}
                    </div>

                    <h4 className="text-sm font-bold text-shop-highlight uppercase tracking-wide">
                      {task.title}
                    </h4>

                    {task.description && (
                      <p className="text-xs text-shop-muted line-clamp-2">{task.description}</p>
                    )}

                    {/* Blocking Reason Callout */}
                    {task.status === 'blocked' && task.blockingReason && (
                      <div className="bg-safety-red/15 border border-safety-red/40 rounded p-2 text-xs text-safety-red flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 shrink-0" />
                        <span>
                          <strong>BLOCKER:</strong> {task.blockingReason}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Technician & Hours Counter */}
                  <div className="flex items-center gap-3 shrink-0 flex-wrap">
                    {/* Assigned Technician */}
                    {task.assignedTo && (
                      <div className="text-xs font-mono text-shop-light bg-shop-darkest px-2.5 py-1.5 rounded border border-shop-border flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-safety-amber" />
                        <span>{task.assignedTo}</span>
                      </div>
                    )}

                    {/* Hours Ledger Box */}
                    <div className="bg-shop-darkest border border-shop-border rounded p-2 text-xs font-mono flex items-center gap-2">
                      <div className="text-right">
                        <div className="text-[10px] text-shop-muted uppercase">Actual / Est</div>
                        <div className="font-bold text-shop-light">
                          <span className="text-safety-amber">{task.actualHours.toFixed(1)}</span> /{' '}
                          {task.estimatedHours.toFixed(1)}h
                        </div>
                        {task.actualHours > 0 && (
                          <div
                            className={`text-[9px] font-bold ${
                              hourDiff <= 0 ? 'text-safety-green' : 'text-safety-red'
                            }`}
                          >
                            {hourDiff <= 0
                              ? `${Math.abs(hourDiff).toFixed(1)}h saved`
                              : `+${hourDiff.toFixed(1)}h over`}
                          </div>
                        )}
                      </div>

                      {/* Hours quick-nudge buttons */}
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleAdjustHours(task, 0.5)}
                          className="min-h-[44px] min-w-[44px] px-2 bg-shop-panel hover:bg-shop-well text-shop-light rounded border border-shop-border text-xs font-mono font-bold leading-none cursor-pointer flex items-center justify-center transition-colors"
                          title="Add 0.5 hour"
                        >
                          +0.5
                        </button>
                        <button
                          type="button"
                          onClick={() => handleAdjustHours(task, -0.5)}
                          className="min-h-[44px] min-w-[44px] px-2 bg-shop-panel hover:bg-shop-well text-shop-light rounded border border-shop-border text-xs font-mono font-bold leading-none cursor-pointer flex items-center justify-center transition-colors"
                          title="Subtract 0.5 hour"
                        >
                          -0.5
                        </button>
                      </div>
                    </div>

                    {/* Action buttons (Min 44px) */}
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleAdvanceStatus(task)}
                        className="min-h-[44px] px-3 bg-shop-panel hover:bg-shop-well text-shop-light border border-shop-border rounded text-xs font-mono font-bold uppercase flex items-center gap-1.5 transition-colors cursor-pointer"
                        title="Cycle status"
                      >
                        <span>Advance</span>
                        <ArrowRight className="w-3.5 h-3.5 text-safety-amber" />
                      </button>

                      <button
                        type="button"
                        onClick={() => handleOpenEditModal(task)}
                        className="min-h-[44px] min-w-[44px] flex items-center justify-center text-shop-muted hover:text-shop-highlight rounded hover:bg-shop-well transition-colors"
                        title="Edit task"
                        aria-label="Edit task"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>

                      <button
                        type="button"
                        onClick={() => setDeletingTask(task)}
                        className="min-h-[44px] min-w-[44px] flex items-center justify-center text-shop-muted hover:text-safety-red rounded hover:bg-shop-well transition-colors"
                        title="Delete task"
                        aria-label="Delete task"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* =========================================================================
          3. ADD TASK MODAL
         ========================================================================= */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-shop-dark border-2 border-safety-amber rounded max-w-lg w-full p-6 space-y-4 shadow-2xl relative my-8">
            <div className="flex items-center justify-between border-b border-shop-border pb-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-safety-amber" />
                <h3 className="text-sm font-bold uppercase tracking-wider text-shop-highlight">
                  Add Restoration Task
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-shop-muted hover:text-shop-highlight"
                aria-label="Close dialog"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveNewTask} className="space-y-4 text-xs font-mono">
              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">Task Title *</label>
                <input
                  type="text"
                  required
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="e.g. Press polyurethane bushings into QA1 upper arms"
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light font-bold focus:border-safety-amber focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Restoration Phase *</label>
                  <select
                    value={formPhase}
                    onChange={(e) => setFormPhase(e.target.value as RestorationPhase)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {Object.keys(RESTORATION_PHASES).map((pk) => {
                      const p = pk as RestorationPhase;
                      return (
                        <option key={p} value={p}>
                          Phase {RESTORATION_PHASES[p].sequence}: {RESTORATION_PHASES[p].title}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Linked Sub-Assembly</label>
                  <select
                    value={formSubAssemblyId}
                    onChange={(e) => setFormSubAssemblyId(e.target.value)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    <option value="">None / General Task</option>
                    {state.subAssemblies.map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        [{sub.binNumber}] {sub.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Status *</label>
                  <select
                    value={formStatus}
                    onChange={(e) => setFormStatus(e.target.value as TaskStatus)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {(Object.keys(STATUS_CONFIG) as TaskStatus[]).map((st) => (
                      <option key={st} value={st}>
                        {STATUS_CONFIG[st].label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Priority *</label>
                  <select
                    value={formPriority}
                    onChange={(e) => setFormPriority(e.target.value as TaskPriority)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {(Object.keys(PRIORITY_CONFIG) as TaskPriority[]).map((pr) => (
                      <option key={pr} value={pr}>
                        {PRIORITY_CONFIG[pr].label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Estimated Hours *</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    required
                    value={formEstimatedHours}
                    onChange={(e) => setFormEstimatedHours(parseFloat(e.target.value) || 0)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Actual Hours</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={formActualHours}
                    onChange={(e) => setFormActualHours(parseFloat(e.target.value) || 0)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-safety-amber font-bold focus:border-safety-amber focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">Assigned Technician</label>
                <input
                  type="text"
                  value={formAssignedTo}
                  onChange={(e) => setFormAssignedTo(e.target.value)}
                  placeholder="e.g. Marcus Vance"
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                />
              </div>

              {formStatus === 'blocked' && (
                <div className="space-y-1">
                  <label className="text-safety-red uppercase block font-bold">
                    Blocking Reason / Missing Components *
                  </label>
                  <input
                    type="text"
                    required
                    value={formBlockingReason}
                    onChange={(e) => setFormBlockingReason(e.target.value)}
                    placeholder="e.g. Awaiting delivery of Grade 8 cam bolts from Summit"
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-safety-red rounded text-safety-red font-bold focus:border-safety-red focus:outline-none"
                  />
                </div>
              )}

              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">Description / Notes</label>
                <textarea
                  rows={2}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Torque sequences, grease fitting priming, alignment specs..."
                  className="w-full px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none font-sans"
                />
              </div>

              <div className="pt-2 flex justify-end gap-3 border-t border-shop-border">
                <MechanicalButton
                  type="button"
                  variant="outline"
                  size="default"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </MechanicalButton>
                <MechanicalButton type="submit" variant="primary" size="default">
                  Save Task
                </MechanicalButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================================
          4. EDIT TASK MODAL
         ========================================================================= */}
      {editingTask && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-shop-dark border-2 border-shop-border rounded max-w-lg w-full p-6 space-y-4 shadow-2xl relative my-8">
            <div className="flex items-center justify-between border-b border-shop-border pb-3">
              <div className="flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-safety-amber" />
                <h3 className="text-sm font-bold uppercase tracking-wider text-shop-highlight">
                  Edit Restoration Task
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setEditingTask(null)}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-shop-muted hover:text-shop-highlight"
                aria-label="Close dialog"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveEditTask} className="space-y-4 text-xs font-mono">
              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">Task Title *</label>
                <input
                  type="text"
                  required
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light font-bold focus:border-safety-amber focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Restoration Phase *</label>
                  <select
                    value={formPhase}
                    onChange={(e) => setFormPhase(e.target.value as RestorationPhase)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {Object.keys(RESTORATION_PHASES).map((pk) => {
                      const p = pk as RestorationPhase;
                      return (
                        <option key={p} value={p}>
                          Phase {RESTORATION_PHASES[p].sequence}: {RESTORATION_PHASES[p].title}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Linked Sub-Assembly</label>
                  <select
                    value={formSubAssemblyId}
                    onChange={(e) => setFormSubAssemblyId(e.target.value)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    <option value="">None / General Task</option>
                    {state.subAssemblies.map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        [{sub.binNumber}] {sub.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Status *</label>
                  <select
                    value={formStatus}
                    onChange={(e) => setFormStatus(e.target.value as TaskStatus)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {(Object.keys(STATUS_CONFIG) as TaskStatus[]).map((st) => (
                      <option key={st} value={st}>
                        {STATUS_CONFIG[st].label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Priority *</label>
                  <select
                    value={formPriority}
                    onChange={(e) => setFormPriority(e.target.value as TaskPriority)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {(Object.keys(PRIORITY_CONFIG) as TaskPriority[]).map((pr) => (
                      <option key={pr} value={pr}>
                        {PRIORITY_CONFIG[pr].label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Estimated Hours *</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    required
                    value={formEstimatedHours}
                    onChange={(e) => setFormEstimatedHours(parseFloat(e.target.value) || 0)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Actual Hours</label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={formActualHours}
                    onChange={(e) => setFormActualHours(parseFloat(e.target.value) || 0)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-safety-amber font-bold focus:border-safety-amber focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">Assigned Technician</label>
                <input
                  type="text"
                  value={formAssignedTo}
                  onChange={(e) => setFormAssignedTo(e.target.value)}
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                />
              </div>

              {formStatus === 'blocked' && (
                <div className="space-y-1">
                  <label className="text-safety-red uppercase block font-bold">
                    Blocking Reason / Missing Components *
                  </label>
                  <input
                    type="text"
                    required
                    value={formBlockingReason}
                    onChange={(e) => setFormBlockingReason(e.target.value)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-safety-red rounded text-safety-red font-bold focus:border-safety-red focus:outline-none"
                  />
                </div>
              )}

              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">Description / Notes</label>
                <textarea
                  rows={2}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  className="w-full px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none font-sans"
                />
              </div>

              <div className="pt-2 flex justify-end gap-3 border-t border-shop-border">
                <MechanicalButton
                  type="button"
                  variant="outline"
                  size="default"
                  onClick={() => setEditingTask(null)}
                >
                  Cancel
                </MechanicalButton>
                <MechanicalButton type="submit" variant="primary" size="default">
                  Save Changes
                </MechanicalButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================================
          5. DELETE CONFIRMATION MODAL
         ========================================================================= */}
      {deletingTask && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-shop-dark border-2 border-safety-red rounded max-w-md w-full p-6 space-y-4 shadow-2xl">
            <h3 className="text-base font-bold uppercase tracking-wider text-safety-red">
              Delete Restoration Task?
            </h3>
            <p className="text-xs text-shop-light leading-relaxed">
              Are you sure you want to delete task &quot;{deletingTask.title}&quot;? Recorded labor hours
              for this task will be permanently removed.
            </p>
            <div className="pt-2 flex justify-end gap-3 border-t border-shop-border">
              <MechanicalButton
                type="button"
                variant="outline"
                size="default"
                onClick={() => setDeletingTask(null)}
              >
                Cancel
              </MechanicalButton>
              <MechanicalButton
                type="button"
                variant="danger"
                size="default"
                onClick={handleConfirmDelete}
              >
                Delete Task
              </MechanicalButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
